import { DEVICE_COLLECTION_NAME } from "verdi-common/schemas/device/device";
import { dayMs } from "verdi-common/timeConstants";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";

type Fragment = NonNullable<ListDataStreams200Item["latestFragment"]>;

function isClosed(fragment: Fragment): boolean {
    return fragment.timeRange.end != null;
}

function ranUnderADay(stream: ListDataStreams200Item, fragment: Fragment): boolean {
    const end = fragment.timeRange.end;
    return end != null && end.getTime() - stream.createdAt.getTime() < dayMs;
}

function deviceKey(fragment: Fragment): string {
    return JSON.stringify([fragment.device.collection, fragment.device.id]);
}

/** Everything that says where a stream's readings come from, apart from the stream id. */
function addressKey(stream: ListDataStreams200Item, fragment: Fragment): string {
    return JSON.stringify([
        deviceKey(fragment),
        fragment.portId,
        stream.unitCategory,
        stream.sensorClusterName,
        stream.sensorElementName,
        fragment.channel,
        fragment.subchannel,
    ]);
}

/**
 * Drops streams the backend left behind, which the data viewer shouldn't offer. Each rule is one
 * backend problem: delete it once the backend cleans that case up, and this file once all are gone.
 */
export function withoutLeftoverStreams(streams: ListDataStreams200Item[]): ListDataStreams200Item[] {
    const open = streams.flatMap((stream) => {
        const fragment = stream.latestFragment;
        return fragment && !isClosed(fragment) ? [{ stream, fragment }] : [];
    });
    const openAddresses = new Set(open.map(({ stream, fragment }) => addressKey(stream, fragment)));
    const devicesWithBoardTemperature = new Set(
        open
            .filter(({ stream, fragment }) => fragment.portId === "I1" && stream.unitCategory === "boardTemperature")
            .map(({ fragment }) => deviceKey(fragment)),
    );

    const isLeftover = (stream: ListDataStreams200Item): boolean => {
        const fragment = stream.latestFragment;
        if (!fragment || !isClosed(fragment)) return false;

        // A new stream was started for the same address; the old one is a splinter if it ran under a day.
        if (openAddresses.has(addressKey(stream, fragment)) && ranUnderADay(stream, fragment)) return true;

        if (fragment.device.collection !== DEVICE_COLLECTION_NAME) return false;

        // The onboard temperature moved from airTemperature to boardTemperature, which started new streams.
        const replacedOnboardTemperature =
            fragment.portId === "I1" &&
            stream.unitCategory === "airTemperature" &&
            devicesWithBoardTemperature.has(deviceKey(fragment));

        // V1 valve state was removed from every device type, and its streams haven't been migrated.
        const retiredValveState = fragment.portId === "V1";

        // The device was deleted; the API still lists its streams, with no host name.
        const deletedDevice = stream.hostName === null;

        return replacedOnboardTemperature || retiredValveState || deletedDevice;
    };

    return streams.filter((stream) => !isLeftover(stream));
}
